/** @format */

import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { Select } from "antd";
import React, { ReactNode, useMemo, useState } from "react";
import { BaseColor } from "../../../../theme/maps/Colors";

interface TEntity {
  id: number;
  name: string;
}

interface Props<T extends TEntity> {
  value?: number | null;
  onChange?: (value: number | null) => void;
  items: T[];
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  /** se presente, digitando un nome che non esiste compare "Crea «…»" */
  onCreate?: (name: string) => Promise<T>;
  /** es. "ruolo" -> "Crea il ruolo «…»" */
  entityLabel: string;
  renderItem?: (item: T) => ReactNode;
  id?: string;
}

/**
 * Select con ricerca che permette di creare al volo un nuovo elemento
 * (ruolo o struttura) senza lasciare il form in cui ci si trova.
 */
export function EntitySelect<T extends TEntity>({
  value,
  onChange,
  items,
  placeholder,
  allowClear,
  disabled,
  onCreate,
  entityLabel,
  renderItem,
  id,
}: Props<T>): JSX.Element {
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);

  const trimmed = search.trim();
  const filtered = useMemo(
    () => items.filter(item => item.name.toLowerCase().includes(trimmed.toLowerCase())),
    [items, trimmed],
  );
  const exactMatch = items.some(item => item.name.trim().toLowerCase() === trimmed.toLowerCase());
  const showCreate = Boolean(onCreate) && trimmed.length > 0 && !exactMatch;

  const create = async (): Promise<void> => {
    if (!onCreate || !showCreate || creating) return;
    setCreating(true);
    try {
      const created = await onCreate(trimmed);
      if (onChange) onChange(created.id);
      setSearch("");
      setOpen(false);
    } catch {
      // il messaggio d'errore è già mostrato da onCreate
    } finally {
      setCreating(false);
    }
  };

  const createRow = showCreate ? (
    <div
      className="org-select-create"
      style={BaseColor ? { color: BaseColor } : undefined}
      role="button"
      tabIndex={0}
      onMouseDown={(e): void => e.preventDefault()}
      onClick={create}
    >
      {creating ? <LoadingOutlined /> : <PlusOutlined />}
      <span>
        Crea {entityLabel} <strong>«{trimmed}»</strong>
      </span>
    </div>
  ) : null;

  return (
    <Select<number | null>
      id={id}
      showSearch
      value={value ?? undefined}
      onChange={(v): void => {
        if (onChange) onChange(v === undefined ? null : v);
      }}
      onClear={(): void => {
        if (onChange) onChange(null);
      }}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled}
      open={open}
      onDropdownVisibleChange={setOpen}
      searchValue={search}
      onSearch={setSearch}
      filterOption={false}
      optionLabelProp="label"
      // Senza opzioni rc-select apre il menu solo se notFoundContent non è vuoto:
      // in quel caso la riga "Crea «…»" prende il posto del messaggio.
      notFoundContent={showCreate ? createRow : trimmed ? "Nessun risultato" : "Nessun elemento"}
      onInputKeyDown={(e): void => {
        if (e.key === "Enter" && filtered.length === 0 && showCreate) {
          e.preventDefault();
          create();
        }
      }}
      dropdownRender={(menu): JSX.Element => (
        <>
          {menu}
          {filtered.length > 0 && createRow}
        </>
      )}
    >
      {filtered.map(item => (
        <Select.Option key={item.id} value={item.id} label={item.name}>
          {renderItem ? renderItem(item) : item.name}
        </Select.Option>
      ))}
    </Select>
  );
}
